import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";
import {
  FLIPKART_IMPORT,
  FLIPKART_ORDERS_LIST,
  FLIPKART_PAYMENTS,
  FLIPKART_PRODUCT_REPORT,
  FLIPKART_STATISTICS,
  FLIPKART_FINANCE_DASHBOARD,
} from "app/routes/constant";
import { userType } from "app/helpers/userType";

const menuItems = [
  {
    title: "Import",
    icon: "material-symbols:deployed-code-update",
    route: FLIPKART_IMPORT,
    color: "#2874F0",
    description: "Upload order sheet (Excel) and view import history",
    permission: "flipkart-import"
  },
  {
    title: "Orders List",
    icon: "material-symbols:list-alt",
    route: FLIPKART_ORDERS_LIST,
    color: "#9C27B0",
    description: "View and manage all Flipkart orders",
    permission: "flipkart-order-list"
  },
  {
    title: "Payment",
    icon: "material-symbols:currency-rupee",
    route: FLIPKART_PAYMENTS,
    color: "#3F51B5",
    description: "View payment received and settlement status",
    permission: "flipkart-payment-list"
  },
  {
    title: "Product Report",
    icon: "material-symbols:analytics",
    route: FLIPKART_PRODUCT_REPORT,
    color: "#009688",
    description: "Product-wise orders, returns, cost and P&L report",
    permission: "flipkart-product-report"
  },
  {
    title: "Statistics",
    icon: "material-symbols:bar-chart",
    route: FLIPKART_STATISTICS,
    color: "#F44336",
    description: "Status-wise product count and summary",
    permission: "flipkart-statistics"
  },
  {
    title: "Finance Dashboard",
    icon: "material-symbols:dashboard",
    route: FLIPKART_FINANCE_DASHBOARD,
    color: "#4CAF50",
    description: "Total profit/loss, sales, settled & pending amounts",
    permission: "flipkart-finance-dashboard"
  },
];

const FlipkartMenu = () => {
  const { isMA } = userType();
  const navigate = useNavigate();

  const [permissionData] = useState(
    JSON.parse(localStorage.getItem("permission")),
  );

  const [permission, setPermission] = useState({
    importPermission: false,
    orderPermission: false,
    paymentPermission: false,
    productPermission: false,
    statisticsPermission: false,
    dashboardPermission: false,
  });

  const hasMenuPermission = (permissionName) => {
    const permissionMap = {
      "flipkart-import": permission.importPermission,
      "flipkart-order-list": permission.orderPermission,
      "flipkart-payment-list": permission.paymentPermission,
      "flipkart-product-report": permission.productPermission,
      "flipkart-statistics": permission.statisticsPermission,
      "flipkart-finance-dashboard": permission.dashboardPermission,
    };

    return Boolean(permissionMap[permissionName]);
  };

  const getPermission = () => {
    if (isMA) {
      setPermission({
        importPermission: true,
        orderPermission: true,
        paymentPermission: true,
        productPermission: true,
        statisticsPermission: true,
        dashboardPermission: true,
      });
    } else {
      let permissionValue = {
        importPermission: false,
        orderPermission: false,
        paymentPermission: false,
        productPermission: false,
        statisticsPermission: false,
        dashboardPermission: false,
      };
      permissionData?.length &&
        permissionData.forEach(function (num) {
          if (num?.name === "flipkart-import") {
            permissionValue.importPermission = true;
          }
          if (num?.name === "flipkart-order-list") {
            permissionValue.orderPermission = true;
          }
          if (num?.name === "flipkart-payment-list") {
            permissionValue.paymentPermission = true;
          }
          if (num?.name === "flipkart-product-report") {
            permissionValue.productPermission = true;
          }
          if (num?.name === "flipkart-statistics") {
            permissionValue.statisticsPermission = true;
          }
          if (num?.name === "flipkart-finance-dashboard") {
            permissionValue.dashboardPermission = true;
          }
        });
      setPermission(permissionValue);
    }
  };

  useEffect(() => {
    getPermission();
  }, []);

  return (
    <Box>
      <Typography variant="h3" sx={{ mb: 3, fontWeight: "bold" }}>
        Flipkart Management
      </Typography>

      <Grid container spacing={3}>
        {menuItems
          .filter((item) => hasMenuPermission(item.permission))
          .map((item, index) => (
          <Grid item xs={12} sm={6} md={4} key={index}>
            <Card
              sx={{
                height: "100%",
                cursor: "pointer",
                transition: "all 0.3s ease",
                "&:hover": {
                  transform: "translateY(-5px)",
                  boxShadow: "0 8px 25px rgba(0,0,0,0.15)",
                },
                border: `2px solid ${item.color}20`,
                borderRadius: 2,
              }}
              onClick={() => navigate(item.route)}
            >
              <CardContent sx={{ textAlign: "center", p: 3 }}>
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: "50%",
                    backgroundColor: `${item.color}15`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 16px auto",
                    border: `3px solid ${item.color}30`,
                  }}
                >
                  <Icon
                    icon={item.icon}
                    style={{ fontSize: "40px", color: item.color }}
                  />
                </Box>

                <Typography
                  variant="h6"
                  sx={{ fontWeight: "bold", mb: 1, color: item.color }}
                >
                  {item.title}
                </Typography>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  {item.description}
                </Typography>

                <Button
                  variant="contained"
                  sx={{
                    backgroundColor: item.color,
                    "&:hover": { backgroundColor: item.color, opacity: 0.8 },
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: "bold",
                  }}
                  startIcon={<Icon icon="material-symbols:arrow-forward" />}
                >
                  Open {item.title}
                </Button>
              </CardContent>
            </Card>
          </Grid>
          ))}
      </Grid>
    </Box>
  );
};

export default FlipkartMenu;
